import { useEffect } from "react"
import { Draft } from "immer"
import { useImmerReducer } from "use-immer"
import { FormAction, FormRecords, FormState, FormContextType } from "./types"
import { createContext } from "../utils"

type GetFormPropsFn = (
  formProps?: Omit<React.AllHTMLAttributes<HTMLFormElement>, "onSubmit">
) => React.AllHTMLAttributes<HTMLFormElement>

type FormRenderProps = FormState &
  Readonly<{
    getFormProps: GetFormPropsFn
  }>

export type RootProps = Readonly<{
  onSubmit: (values: FormRecords) => void
  disabled?: (values: FormRecords) => boolean
  children: (renderProps: FormRenderProps) => React.ReactNode
}>

const [Provider, useContext] = createContext<FormContextType>(`Form`)

export const useForm = useContext

export const Root = ({ children, disabled, onSubmit }: RootProps) => {
  const [state, dispatch] = useImmerReducer(reducer, {
    disabled: false,
    values: {},
  })

  useEffect(() => {
    if (disabled) {
      dispatch({
        type: `setFormDisabled`,
        state: disabled(state.values),
      })
    }
  }, [state.values, disabled, dispatch])

  const getFormProps: GetFormPropsFn = (formProps) => ({
    ...formProps,
    disabled: state.disabled,
    onSubmit: (event) => {
      event.preventDefault()
      return !state.disabled && onSubmit(state.values)
    },
  })

  return (
    <Provider state={state} dispatch={dispatch}>
      {children({ getFormProps, ...state })}
    </Provider>
  )
}

function reducer(state: Draft<FormState>, action: FormAction): FormState {
  switch (action.type) {
    case `setFormRecord`:
      state.values[action.field] = action.record
      return state
    case `setFormDisabled`:
      state.disabled = action.state
      return state
    default:
      return state
  }
}
